import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

function SellerReturnsPanel({
  getAuthHeaders,
  getApiBaseUrl,
  enforcedOwnerRoute = '',
  ownerRouteLabel = '',
  initialCaseId = '',
  onInitialCaseOpened,
}) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [returnTab, setReturnTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCaseId, setSelectedCaseId] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [decision, setDecision] = useState('approve');
  const [reason, setReason] = useState('');
  const lastOpenedCaseIdRef = useRef('');

  const formatDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatMoney = (amount) => {
    const value = Number(amount || 0);
    return `$${value.toFixed(2)}`;
  };

  const formatAuditActionLabel = (actionType) => {
    const labels = {
      case_created: 'Case created',
      case_acknowledged: 'Case acknowledged',
      evidence_added: 'Evidence added',
      owner_decision: 'Owner decision',
      governance_decision: 'Platform decision',
      requested_more_info: 'Requested more info',
      escalated: 'Escalated',
      refund_processed: 'Refund processed',
      assigned: 'Assigned',
      late_return_honored: 'Late return honored',
    };

    return labels[actionType] || String(actionType || 'event').replace(/_/g, ' ');
  };

  const formatStatusLabel = (status) => {
    const labels = {
      none: 'Created',
      requested: 'Requested',
      awaiting_return_receipt: 'Awaiting return receipt',
      return_acknowledged: 'Return acknowledged',
      seller_review: 'Seller review',
      under_review: 'Under review',
      requested_info: 'Requested info',
      platform_review: 'Platform review',
      approved_for_refund: 'Approved for refund',
      denied: 'Denied',
      refund_processing: 'Refund processing',
      refund_processed: 'Refund processed',
      refunded: 'Refunded',
      expired: 'Expired',
    };

    return labels[status] || String(status || 'N/A').replace(/_/g, ' ');
  };

  const isRefundedStatus = (status) => ['refund_processed', 'refunded'].includes(status);

  const isPendingRefundStatus = (status) => (
    !isRefundedStatus(status) && !['denied', 'expired', 'closed'].includes(status)
  );

  const isTerminalReturnStatus = (status) => (
    ['denied', 'refund_processed', 'refunded', 'expired', 'closed'].includes(status)
  );

  const fetchCases = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (enforcedOwnerRoute) params.set('ownerRoute', enforcedOwnerRoute);
      params.set('limit', '100');

      const response = await axios.get(
        `${baseURL}/api/product-returns/owner/cases?${params.toString()}`,
        { headers },
      );

      setCases(Array.isArray(response.data?.cases) ? response.data.cases : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load return cases.');
    } finally {
      setLoading(false);
    }
  }, [enforcedOwnerRoute, getAuthHeaders, getApiBaseUrl]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const openCase = useCallback(async (caseId) => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      setError('');
      setSelectedCaseId(caseId);

      const response = await axios.get(
        `${baseURL}/api/product-returns/owner/cases/${caseId}`,
        { headers },
      );

      setSelectedCase(response.data?.case || null);
      setAuditRows(Array.isArray(response.data?.audit) ? response.data.audit : []);
      setDecision('approve');
      setReason('');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load case details.');
    }
  }, [getAuthHeaders, getApiBaseUrl]);

  useEffect(() => {
    if (initialCaseId && lastOpenedCaseIdRef.current !== initialCaseId) {
      lastOpenedCaseIdRef.current = initialCaseId;
      let cancelled = false;
      Promise.resolve(openCase(initialCaseId)).finally(() => {
        if (!cancelled && typeof onInitialCaseOpened === 'function') {
          onInitialCaseOpened(initialCaseId);
        }
      });

      return () => {
        cancelled = true;
      };
    }
  }, [initialCaseId, onInitialCaseOpened, openCase]);

  const closeCase = () => {
    setSelectedCaseId('');
    setSelectedCase(null);
    setAuditRows([]);
    setReason('');
  };

  const submitDecision = async () => {
    if (!selectedCaseId) {
      return;
    }

    if (decision === 'deny' && !reason.trim()) {
      setError('Decision reason is required.');
      return;
    }

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      setSaving(true);
      setError('');

      const response = await axios.put(
        `${baseURL}/api/product-returns/owner/cases/${selectedCaseId}/decision`,
        {
          decision,
          reason: decision === 'approve' ? 'Seller approved refund' : reason.trim(),
        },
        { headers },
      );

      setSelectedCase(response.data?.case || selectedCase);
      await fetchCases();
      await openCase(selectedCaseId);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to apply decision.');
    } finally {
      setSaving(false);
    }
  };

  const markReceived = async () => {
    if (!selectedCaseId) return;

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      setSaving(true);
      setError('');

      await axios.put(
        `${baseURL}/api/product-returns/owner/cases/${selectedCaseId}/mark-received`,
        {},
        { headers },
      );

      await fetchCases();
      await openCase(selectedCaseId);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to confirm return receipt.');
    } finally {
      setSaving(false);
    }
  };

  const acknowledgeCase = async () => {
    if (!selectedCaseId) return;

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      setSaving(true);
      setError('');

      await axios.put(
        `${baseURL}/api/product-returns/owner/cases/${selectedCaseId}/acknowledge`,
        { note: reason.trim() },
        { headers },
      );

      await fetchCases();
      await openCase(selectedCaseId);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to acknowledge return case.');
    } finally {
      setSaving(false);
    }
  };

  const honorLateReturn = async () => {
    if (!selectedCaseId || !reason.trim()) {
      setError('Late-return override reason is required.');
      return;
    }

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      setSaving(true);
      setError('');

      await axios.put(
        `${baseURL}/api/product-returns/owner/cases/${selectedCaseId}/honor-late-return`,
        { reason: reason.trim() },
        { headers },
      );

      await fetchCases();
      await openCase(selectedCaseId);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to honor late return.');
    } finally {
      setSaving(false);
    }
  };

  const returnTabCounts = useMemo(() => ({
    all: cases.length,
    pending: cases.filter((row) => isPendingRefundStatus(row?.status)).length,
    refunded: cases.filter((row) => isRefundedStatus(row?.status)).length,
  }), [cases]);

  const filteredCases = useMemo(() => {
    const tabFilteredCases = cases.filter((row) => {
      if (returnTab === 'pending') return isPendingRefundStatus(row?.status);
      if (returnTab === 'refunded') return isRefundedStatus(row?.status);
      return true;
    });

    if (!searchTerm.trim()) return tabFilteredCases;

    const term = searchTerm.trim().toLowerCase();
    return tabFilteredCases.filter((row) => {
      const caseNumber = String(row?.caseNumber || '').toLowerCase();
      const orderNumber = String(row?.orderNumber || '').toLowerCase();
      return caseNumber.includes(term) || orderNumber.includes(term);
    });
  }, [cases, returnTab, searchTerm]);

  return (
    <div>
      <div className="orders-header" style={{ marginBottom: '16px' }}>
        <h1 style={{ marginBottom: '6px' }}>Return Cases</h1>
        <p className="orders-subtitle">
          Review and action product return requests
          {enforcedOwnerRoute
            ? ` - Scope ${ownerRouteLabel ? `${ownerRouteLabel} (${enforcedOwnerRoute})` : enforcedOwnerRoute}`
            : ''}
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="orders-filters">
        <div className="filter-tabs" style={{ gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className={`filter-tab ${returnTab === 'all' ? 'active' : ''}`}
            onClick={() => setReturnTab('all')}
          >
            All Returns <span className="tab-count">{returnTabCounts.all}</span>
          </button>
          <button
            type="button"
            className={`filter-tab ${returnTab === 'pending' ? 'active' : ''}`}
            onClick={() => setReturnTab('pending')}
          >
            Pending Refund <span className="tab-count">{returnTabCounts.pending}</span>
          </button>
          <button
            type="button"
            className={`filter-tab ${returnTab === 'refunded' ? 'active' : ''}`}
            onClick={() => setReturnTab('refunded')}
          >
            Refunded <span className="tab-count">{returnTabCounts.refunded}</span>
          </button>

          <button className="btn-view" onClick={fetchCases}>
            Refresh
          </button>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="Search by case # or order #"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="empty-state">
          <div className="empty-icon">...</div>
          <h3>Loading return cases...</h3>
        </div>
      ) : filteredCases.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">Return</div>
          <h3>No return cases found</h3>
          <p>Cases will appear here once customers submit return requests.</p>
        </div>
      ) : (
        <div className="orders-list">
          {filteredCases.map((row) => (
            <div key={row._id} className="order-card returns-case-card">
              <div className="returns-case-row">
                <div className="returns-case-column">
                  <div className="returns-case-primary-line">
                    <span className="returns-case-status">({String(row.status || 'unknown').replace(/_/g, ' ')})</span>
                    <span className="returns-case-order">Order {row.orderNumber || 'N/A'}</span>
                  </div>
                  <div className="returns-case-number">{row.caseNumber || 'N/A'}</div>
                </div>

                <div className="returns-case-column">
                  <div className="returns-case-label">Requested At</div>
                  <div className="returns-case-value">{formatDate(row.requestedAt || row.createdAt)}</div>
                </div>

                <div className="returns-case-column">
                  <div className="returns-case-label">Requested Amount</div>
                  <div className="returns-case-amount">{formatMoney(row?.financial?.amountRequested)}</div>
                </div>

                <div className="returns-case-column returns-case-action-column">
                  <button className="btn-view" onClick={() => openCase(row._id)}>
                    View Case
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedCase && (
        <div className="modal-overlay" onClick={closeCase}>
          <div className="modal-content returns-modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header returns-modal-header">
              <div>
                <p className="returns-modal-kicker">Return Case</p>
                <h2>Case {selectedCase.caseNumber || ''}</h2>
              </div>
              <button className="modal-close" onClick={closeCase}>
                x
              </button>
            </div>

            <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
              <div className="returns-case-meta-grid">
                <div className="returns-meta-card">
                  <span>Order Number</span>
                  <strong>{selectedCase.orderNumber || 'N/A'}</strong>
                </div>
                <div className="returns-meta-card">
                  <span>Status</span>
                  <strong>{selectedCase.status || 'N/A'}</strong>
                </div>
                <div className="returns-meta-card">
                  <span>Requested At</span>
                  <strong>{formatDate(selectedCase.requestedAt || selectedCase.createdAt)}</strong>
                </div>
                <div className="returns-meta-card">
                  <span>Return Due</span>
                  <strong>{formatDate(selectedCase.returnDueAt)}</strong>
                </div>
                <div className="returns-meta-card">
                  <span>Physical Receipt</span>
                  <strong>{selectedCase.physicallyReceivedAt ? formatDate(selectedCase.physicallyReceivedAt) : 'Not confirmed'}</strong>
                </div>
                <div className="returns-meta-card">
                  <span>Requested Amount</span>
                  <strong>{formatMoney(selectedCase?.financial?.amountRequested)}</strong>
                </div>
              </div>

              {(selectedCase.ackDueAt || selectedCase.expirationReason) && (
                <section className="returns-modal-section">
                  <p className="returns-section-title">Policy Timing</p>
                  {selectedCase.ackDueAt ? (
                    <div className="detail-row">
                      <strong>Acknowledge By:</strong> {formatDate(selectedCase.ackDueAt)}
                    </div>
                  ) : null}
                  {selectedCase.expirationReason ? (
                    <div className="detail-row returns-description-row">
                      <strong>Expiration Reason:</strong> {String(selectedCase.expirationReason).replace(/_/g, ' ')}
                    </div>
                  ) : null}
                </section>
              )}

              <section className="returns-modal-section">
                <p className="returns-section-title">Request Summary</p>
                <div className="detail-row">
                  <strong>Reason:</strong> {selectedCase.requestReason || 'N/A'}
                </div>
                {selectedCase.requestDescription && (
                  <div className="detail-row returns-description-row">
                    {selectedCase.requestDescription}
                  </div>
                )}
                {selectedCase.eligibilitySnapshot && (
                  <div className="detail-row returns-description-row" style={{ marginTop: '8px' }}>
                    <strong>Eligibility:</strong>{' '}
                    {selectedCase.eligibilitySnapshot.eligible ? 'Eligible' : 'Ineligible'}
                    {' · '}
                    <strong>Window:</strong>{' '}
                    {selectedCase.eligibilitySnapshot.windowDays ?? 'N/A'} day(s)
                    {' · '}
                    <strong>Policy:</strong>{' '}
                    {selectedCase.eligibilitySnapshot.policySource || 'N/A'}
                  </div>
                )}
              </section>

              <section className="returns-modal-section">
                <p className="returns-section-title">Item Condition</p>
                <div className="detail-row">
                  <strong>Used:</strong>{' '}
                  {selectedCase.conditionAttestation?.itemUsedStatus || 'unknown'}
                </div>
                <div className="detail-row">
                  <strong>Package Opened:</strong>{' '}
                  {selectedCase.conditionAttestation?.packageOpenedStatus || 'unknown'}
                </div>
                <div className="detail-row">
                  <strong>Damaged:</strong>{' '}
                  {selectedCase.conditionAttestation?.itemDamagedStatus || 'unknown'}
                </div>
                {selectedCase.conditionAttestation?.reasonDetail && (
                  <div className="detail-row returns-description-row">
                    {selectedCase.conditionAttestation.reasonDetail}
                  </div>
                )}
              </section>

              <section className="returns-modal-section">
                <p className="returns-section-title">Items</p>
                {(selectedCase.items || []).length === 0 ? (
                  <div className="detail-row">No items attached.</div>
                ) : (
                  <div className="returns-list-grid">
                    {(selectedCase.items || []).map((item, idx) => (
                      <div key={`${item.orderItemId || 'item'}-${idx}`} className="returns-list-card">
                        <div className="returns-list-title">
                          {item.productName || item.name || 'Item'}
                        </div>
                        <div className="returns-list-subtitle">
                          Qty: {item.quantityRequested || item.quantity || 1}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="returns-modal-section">
                <p className="returns-section-title">Evidence</p>
                {(selectedCase.evidence || []).length === 0 ? (
                  <div className="detail-row">No evidence uploaded.</div>
                ) : (
                  <div className="returns-list-grid">
                    {(selectedCase.evidence || []).map((entry, idx) => (
                      <div key={`${entry.url || 'evidence'}-${idx}`} className="returns-list-card">
                        <a href={entry.url} target="_blank" rel="noopener noreferrer">
                          Evidence {idx + 1}
                        </a>
                        {entry.note ? <div className="returns-list-subtitle">{entry.note}</div> : null}
                        {entry.caption ? <div className="returns-list-subtitle">{entry.caption}</div> : null}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="returns-modal-section">
                <p className="returns-section-title">Audit Timeline</p>
                {auditRows.length === 0 ? (
                  <div className="detail-row">No audit events.</div>
                ) : (
                  <div className="returns-audit-list">
                    {auditRows.map((row) => (
                      <div key={row._id} className="returns-audit-item">
                        <div className="returns-audit-head">
                          <div className="returns-audit-title">
                            {formatAuditActionLabel(row.actionType)}
                          </div>
                          <div className="returns-audit-date">{formatDate(row.createdAt)}</div>
                        </div>
                        {row.reason && <div className="returns-audit-reason">{row.reason}</div>}
                        {row.previousStatus || row.newStatus ? (
                          <div className="returns-audit-transition">
                            {formatStatusLabel(row.previousStatus)} to {formatStatusLabel(row.newStatus)}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="returns-modal-section returns-decision-section">
                <p className="returns-section-title">Decision</p>
                {isTerminalReturnStatus(selectedCase.status) ? (
                  <div className="detail-row" style={{ display: 'block' }}>
                    This return case is already {formatStatusLabel(selectedCase.status).toLowerCase()}.
                  </div>
                ) : (
                  <>
                    <div className="detail-row" style={{ display: 'block' }}>
                      <select
                        value={decision}
                        onChange={(e) => setDecision(e.target.value)}
                        className="search-input"
                        style={{ maxWidth: '240px' }}
                      >
                        <option value="approve">Approve</option>
                        <option value="deny">Deny</option>
                      </select>
                    </div>

                    {decision === 'deny' && (
                      <div className="detail-row" style={{ display: 'block' }}>
                        <textarea
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          rows={3}
                          placeholder="Denial reason (required)"
                          className="search-input"
                          style={{ width: '100%' }}
                        />
                      </div>
                    )}
                  </>
                )}
              </section>
            </div>

            <div className="modal-footer">
              {['requested', 'awaiting_return_receipt'].includes(selectedCase.status) && !selectedCase.acknowledgedAt ? (
                <button className="btn-view" onClick={acknowledgeCase} disabled={saving}>
                  {saving ? 'Saving...' : 'Acknowledge Return'}
                </button>
              ) : null}
              {selectedCase.status === 'expired' && selectedCase.expirationReason === 'not_returned_in_window' ? (
                <button className="btn-view" onClick={honorLateReturn} disabled={saving}>
                  {saving ? 'Saving...' : 'Honor Late Return'}
                </button>
              ) : null}
              {!selectedCase.physicallyReceivedAt && !['denied', 'refund_processed', 'refunded', 'expired'].includes(selectedCase.status) ? (
                <button className="btn-view" onClick={markReceived} disabled={saving}>
                  {saving ? 'Saving...' : 'Confirm Physical Receipt'}
                </button>
              ) : null}
              {!isTerminalReturnStatus(selectedCase.status) ? (
                <button className="btn-mark-pickup" onClick={submitDecision} disabled={saving}>
                  {saving ? 'Saving...' : decision === 'approve' ? 'Approve & Refund' : 'Submit Decision'}
                </button>
              ) : null}
              <button className="btn-secondary" onClick={closeCase}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SellerReturnsPanel;
